@import 'fluid-combo-box.theme.less';
@import '../../styles/dropdown-menu.styles.less';

@import '../../styles/mixins/fluid-fading-animations.mixin.less';
@import '../../styles/mixins/fluid-transitions.mixin.less';
@import '../../styles/mixins/fluid-form-elements.mixin.less';

// ================================================================ //
// -- Theme Definition

:host {
  display: block;

  *,
  *::after,
  *::before {
    box-sizing: border-box;
  }

  .fluid-combo-box {

    /**
     * LEGACY THEME
     */
    &.legacy {
      @import '../../styles/variables-iuw-legacy.less';
      @combo-box-tokens: @combo-box-tokens-legacy;
      @dropdown-menu();
      @option-chip();
      @combo-box();
    }

    /**
     * LIBERTY MUTUAL THEME
     */
    &.lm {
      @import '../../styles/variables-lm.less';
      @combo-box-tokens: @combo-box-tokens-lm;
      @dropdown-menu();
      @option-chip();
      @combo-box();
    }

    /**
     * LM CORPORATE THEME
     */
    &.corp, &.default {
      @import '../../styles/variables-lm-corp.less';
      @combo-box-tokens: @combo-box-tokens-corp;
      @dropdown-menu();
      @option-chip();
      @combo-box();
    }

    /**
     * PORTAL THEME
     */
    &.portal {
      @import '../../styles/variables-lm-portal.less';
      @combo-box-tokens: @combo-box-tokens-portal;
      @dropdown-menu();
      @option-chip();
      @combo-box();
    }
  }
}


// ================================================================ //
// -- Component Detached Ruleset: Combo Box

@combo-box: {
  @content-offset: @combo-box-tokens[@content-offset];
  @button-size: @form-field-button-size;
  @option: @combo-box-tokens[@option];
  @option-selected: @option[@selected];
  @chip: @combo-box-tokens[@chip];

  // ==== Computed Variables

  @container-padding-y: .form-field-padding-y(
    @font-size: @combo-box-tokens[font-size];
    @line-height: @combo-box-tokens[line-height];
    @offset: @content-offset;
  );

  // ---------------- //

  font-family: @font-family;
  font-size: @combo-box-tokens[font-size];
  font-weight: @combo-box-tokens[font-weight];
  line-height: @combo-box-tokens[line-height];
  color: @combo-box-tokens[text-color];

  // ==== Form Group

  .form-group { .fluid-form-group(); }

  // ==== Container

  .combo-box-container {
    @container-padding-y();
    width: 100%;
    min-height: @form-field-height;
    position: relative;
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    padding-left: @form-field-padding-left - @content-offset;
    padding-right: (@form-field-padding-right - @form-field-button-offset - @content-offset) + @button-size;
    background-color: @white;
    border-width: @border-width;
    border-style: @border-style;
    border-color: @color-form-field-border;
    border-radius: @border-radius;
    box-shadow: @combo-box-tokens[box-shadow];
    cursor: text;

    .transition(@property: border-color; @duration: short);

    // == Hover & Focus States

    &:not(.disabled) {
      &:hover:not(.read-only),
      &:focus-within:not(.read-only),
      &.has-focus {
        border-color: @color-form-field-border-hover;
      }
    }

    // == Contains Selected Option(s)

    &.has-selected {

      &.allow-cancel { // Offset both buttons
        padding-right: @form-field-button-offset + (@button-size * 2);
      }
    }

    // == Errors in selected Option(s)

    &.has-error {
      border-color: @color-error;
    }

    // == Read Only

    &.read-only {
      padding-left: 0;
      border-color: transparent;
      background-color: transparent;
      box-shadow: none;

      .menu-trigger {
        display: none;
      }
    }

    // == Disabled

    &.disabled {
      background-color: @form-field-background-disabled;
      border-color: @form-field-border-color-disabled;
      color: @form-field-color-disabled;
      cursor: not-allowed;

      .menu-trigger {
        cursor: not-allowed;
      }
    }
  }

  // ==== Input

  .combo-box-input {
    @form-field-reset();
    width: 0;
    min-width: 3em;
    flex: 1 0 auto;
    line-height: inherit;
    text-overflow: ellipsis;
    padding: @content-offset;
    outline: none;

    .placeholder-text();

    &.disabled {
      cursor: not-allowed;
    }

    &.read-only {
      padding-left: 0;
    }

    // TODO: REPLACE WITH COMMON RULE AFTER LESS => SCSS CONVERSION
    &.sr-only {
      width: 1px !important;
      height: 1px !important;
      position: absolute !important;
      clip-path: inset(50%) !important;
      overflow: hidden !important;
      white-space: nowrap !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      user-select: none !important;
    }
  }

  // ==== Readonly Input Text (when "readonlyTextOverflow" is "wrap")

  .readonly-input-value {
    display: block;
  }

  // ==== Input Controls Container

  .combo-box-controls {
    display: inline-flex;
    position: absolute;
    right: @form-field-button-offset;
    top: 50%;
    transform: translateY(-50%);
  }

  // ==== Options Loading Indicator

  .menu-loading {
    width: @button-size;
    height: @button-size;
    align-items: center;
    justify-content: center;
    display: inline-flex;
    position: relative;
  }

  // ==== Dropdown Menu Trigger

  .menu-trigger {
    @button-reset();
    width: @button-size;
    height: @button-size;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    color: @color-text; // Sets FLUID Icon "fill" color
  }

  // ==== Focus Indicator

  .focus-indicator(@input-styles: false);

  // ==== Dropdown Menu

  .fluid-dropdown-menu {
    width: 100%;
    max-height: 40vh;
    overflow-y: auto;
    padding-top: 0;
    padding-bottom: 0;
    contain: content;

    &.fixed-position {
      will-change: transform;
    }
  }

  // ==== Dropdown Menu List

  .dropdown-menu-list {
    padding-top: @menu-padding-top;
    padding-bottom: @menu-padding-bottom;
  }

  // ==== Dropdown Menu Option

  .dropdown-menu-option {

    // == Hover / Active States

    &:hover,
    &.dropdown-menu-option--active {
      background-color: @menu-item-hover-background;
    }

    // == Disabled

    &.dropdown-menu-option--disabled {
      cursor: not-allowed;
      background: none;
      outline: 0;
      opacity: 0.5;
    }

    // == Selected State

    &.dropdown-menu-option--selected {
      background-color: @option-selected[background-color];

      &:hover,
      &.dropdown-menu-option--active {
        background-color: darken(@option-selected[background-color], 5%);
      }
    }
  }

  // ==== Dropdown Menu Option Label

  .dropdown-menu-option-label {}

  .dropdown-menu-option--message {
    column-gap: 0.5em;

    fluid-icon {
      flex: 0 1 auto;
    }

    .dropdown-menu-option-label {
      flex: 0 1 auto;
    }
  }
};


// ================================================================ //
// -- Component Detached Ruleset: Chip

@option-chip: {
  @chip: @combo-box-tokens[@chip];
  @chip-disabled: @chip[@disabled];
  @content-offset: @combo-box-tokens[@content-offset];
  @chip-padding: 2px;

  .option-chip {
    min-width: 0;
    max-width: calc(100% - (@content-offset * 2));
    display: inline-flex;
    justify-content: center;
    align-items: center;
    position: relative;
    background-color: @chip[background-color];
    border-width: @border-width;
    border-color: @chip[border-color];
    border-style: @border-style;
    border-radius: @chip[border-radius];
    margin: @content-offset;
    padding-top: @chip-padding;
    padding-bottom: @chip-padding;
    padding-left: @chip[padding-x];
    user-select: none;
    cursor: auto;

    &.chip-active {}

    &.read-only,
    &.disabled {
      padding-right: @chip[padding-x];
    }

    &.read-only {
      user-select: auto;
    }

    &.overflow-wrap {
      height: unset;

      .chip-label {
        white-space: normal;
        text-overflow: unset;
        overflow: unset;
      }
    }

    &.disabled {
      background-color: @chip-disabled[background-color];
      cursor: not-allowed;
    }
  }

  .chip-label {
    display: block;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    font-size: @chip[font-size];
    line-height: calc(1lh - (@border-width * 2) - (@chip-padding * 2));
  }

  .chip-dismiss-button {
    @button-reset();
    height: 100%;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding-left: @chip[padding-x] / 1.4;
    padding-right: @chip[padding-x] / 1.4;

    &:hover {
      fluid-icon { opacity: 0.6; }
    }

    &.dismiss-hidden {
      display: none;
    }

    fluid-icon {
      color: inherit;
      opacity: 0.4;

      .transition(@property: opacity; @duration: 100ms);
    }
  }
};
